import React, { useMemo, useRef, useState } from "react";
import { Navigate } from "react-router";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useShallow } from "zustand/react/shallow";
import { ModelElementType } from "@/components/entities";
import { useAuth } from "@/contexts/AuthProvider";
import { SearchBar } from "@/components/common/SearchBar";
import { SectionRule } from "components/common/SectionRule";
import { ModelRow } from "@/components/main/overview/ModelRow";
import { ModelRows } from "@/components/main/overview/ModelRows";
import { ModelsLoading } from "@/components/main/overview/ModelsLoading";
import { selectCatalogModels } from "@/components/main/overview/selectors";
import { ConfirmDialog } from "@/components/main/utils/ConfirmDialog";
import { MetaAddButton } from "@/components/main/datasets/DatasetMetaSection";
import { useCreateModel } from "@/components/main/utils/models/useCreateModel";
import { NewModelOverlay } from "@/components/main/overview/NewModelOverlay";
import { useModelsPolling } from "@/components/main/hooks/useModelsPolling";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import { deletionCountdown } from "@/lib/dates";
import { ColumnTopSpacer, ListBox } from "@/components/main/pages/pageColumn";

/// The only list of catalog models: they belong to no org, so the Overview and
/// the org Trash leave them out.
export const CatalogModelsPage: React.FC = () => {
  const { canManagePublicCatalog } = useAuth();
  const { isOnline, modelsInitialized } = useModelsPolling({
    catalogTrash: canManagePublicCatalog,
  });
  const {
    availableModels,
    catalogTrash,
    catalogTrashError,
    canDelete,
    fetchCatalogTrash,
    restoreModel,
    purgeModel,
  } = useMainStateStore(
    useShallow((s) => ({
      availableModels: s.availableModels,
      catalogTrash: s.catalogTrash,
      catalogTrashError: s.catalogTrashError,
      canDelete: s.canDeleteCatalogModels,
      fetchCatalogTrash: s.fetchCatalogTrash,
      restoreModel: s.restoreModel,
      purgeModel: s.purgeModel,
    })),
  );
  const { freshModelId, createModel } = useCreateModel();
  const paneRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [naming, setNaming] = useState(false);

  const [filter, setFilter] = useState("");
  // `null` until toggled: collapsed, unless a search only the Trash answers.
  const [trashOpen, setTrashOpen] = useState<boolean | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<ModelElementType | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);

  const shown = useMemo(
    () =>
      selectCatalogModels(
        availableModels.filter((m) => m.is_catalog),
        catalogTrash ?? [],
        filter,
      ),
    [availableModels, catalogTrash, filter],
  );
  const q = filter.trim();
  const trashExpanded =
    trashOpen ?? (!!q && shown.live.length === 0 && shown.trashed.length > 0);
  // Stale rows beat an error line: only a Trash never read reports failing.
  const trashFailed = catalogTrash === null && catalogTrashError;

  const restore = async (model: ModelElementType) => {
    const outcome = await restoreModel(model.model_id);
    showNotificationGlobal(
      outcome.ok ? "Model restored from trash" : outcome.message,
      outcome.ok ? "success" : "error",
    );
  };

  const confirmDelete = async () => {
    if (!confirmTarget) return;
    setDeleting(true);
    const ok = await purgeModel(confirmTarget.model_id);
    setDeleting(false);
    setConfirmTarget(null);
    showNotificationGlobal(
      ok ? "Model permanently deleted" : "Failed to permanently delete model",
      ok ? "success" : "error",
    );
  };

  if (!canManagePublicCatalog) return <Navigate to="/overview" replace />;
  const loading =
    !modelsInitialized || (catalogTrash === null && !catalogTrashError);

  const hasTrash = (catalogTrash?.length ?? 0) > 0 || trashFailed;

  return (
    <div
      ref={paneRef}
      className="relative flex flex-col items-center w-full h-full overflow-y-auto px-6"
      style={{
        opacity: isOnline ? 1 : 0.55,
        transition: "opacity var(--duration-short) var(--ease-move)",
      }}
    >
      <ColumnTopSpacer />
      <div
        className="w-full flex flex-col"
        style={{
          maxWidth: "45rem",
          paddingBottom: "2.5rem",
        }}
      >
        <div className="shrink-0 mb-6">
          <SearchBar
            value={filter}
            onChange={setFilter}
            placeholder="Search catalog models"
            size="lg"
          />
        </div>
        <ListBox ref={listRef}>
          <div className="sticky top-0 z-10 bg-bg overflow-hidden">
            <SectionRule
              inset
              icon={<PublicOutlinedIcon sx={{ fontSize: 14 }} />}
              label="Catalog models"
            />
          </div>
          {shown.live.length > 0 ? (
            <ModelRows models={shown.live} freshModelId={freshModelId} />
          ) : loading ? (
            <ModelsLoading
              isOnline={isOnline}
              label="Loading catalog models…"
            />
          ) : (
            <div className="py-12 text-center text-small text-fg-muted italic">
              {q
                ? `Nothing matches “${q}”${shown.trashed.length > 0 ? " outside the Trash" : ""}.`
                : "No catalog models yet."}
            </div>
          )}
        </ListBox>
        <div className="flex justify-end mt-6">
          <MetaAddButton
            label="New catalog model"
            className="naming-opener"
            onClick={() => setNaming(true)}
            ariaHasPopup="dialog"
            ariaExpanded={naming}
          />
        </div>
        <NewModelOverlay
          open={naming}
          target={{ catalog: true }}
          anchorRef={listRef}
          paneRef={paneRef}
          createModel={createModel}
          onClose={() => setNaming(false)}
        />

        {hasTrash && (
          <div className="mt-8" data-testid="catalog-trash">
            <SectionRule
              inset
              icon={<DeleteOutlineOutlinedIcon sx={{ fontSize: 14 }} />}
              label="Trash"
              count={trashFailed ? undefined : shown.trashed.length}
              action={
                trashFailed
                  ? {
                      label: "Retry",
                      onClick: () => void fetchCatalogTrash(),
                    }
                  : {
                      label: trashExpanded ? "Hide" : "Show",
                      onClick: () => setTrashOpen(!trashExpanded),
                      expanded: trashExpanded,
                      testId: "toggle-catalog-trash",
                    }
              }
            />
            {trashFailed ? (
              <div className="px-1 text-small text-fg-muted italic">
                Couldn&apos;t load the trashed catalog models.
              </div>
            ) : (
              trashExpanded && (
                <ListBox>
                  {shown.trashed.map((m) => {
                    const countdown = m.scheduled_deletion_date
                      ? deletionCountdown(m.scheduled_deletion_date)
                      : { label: "", tooltip: "" };
                    return (
                      <ModelRow
                        key={m.model_id}
                        model={m}
                        trash={{
                          countdownLabel: countdown.label,
                          countdownTooltip: countdown.tooltip,
                          onRestore: () => void restore(m),
                          onDeleteForever: () => setConfirmTarget(m),
                          restoreLocked: canDelete
                            ? undefined
                            : "Only a publisher restores a catalog model",
                          deleteForeverLocked: !canDelete,
                        }}
                      />
                    );
                  })}
                  {shown.trashed.length === 0 && (
                    <div className="px-1 py-2 text-small text-fg-muted italic">
                      Nothing in the Trash matches “{q}”.
                    </div>
                  )}
                </ListBox>
              )
            )}
          </div>
        )}
      </div>
      <ConfirmDialog
        open={confirmTarget !== null}
        title="Permanently delete model"
        message={
          <p>
            Permanently delete{" "}
            <span className="text-fg-bold">
              {confirmTarget?.name || "Unnamed Model"}
            </span>
            ? This can&apos;t be undone.
          </p>
        }
        confirmText="Delete forever"
        confirmVariant="destructive"
        isLoading={deleting}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
};
